import { Link } from 'react-router-dom';
import { Lock, Building2, MapPin } from 'lucide-react';
import { useAccess, tierOf, isMemberTier } from '@/components/dashboard/access';
import { GateLoading, GateError } from '@/components/dashboard/PremiumOnly';

/*
  Sección solo para socios (decisión de Samuel, 08/10/2026).

  Oportunidades es lo que da sentido al club una vez dentro. Un usuario sin membresía («free»)
  ve la sección en el menú, pero al entrar la ve «censurada» (difuminada, con datos de relleno)
  y una invitación a hacerse socio. El servidor tampoco le envía datos (403 en /opportunities).
  Es el único bloqueo con vista difuminada; el resto usa AccessGate (PremiumOnly.jsx) y la matriz de access.js.
*/
export const MEMBER_PLANS = ['starter', 'basica', 'premium'];

export function hasMemberAccess({ user, membership, isAdmin }) {
  if (!user) return false;
  return isMemberTier(tierOf({ user, membership, isAdmin }));
}

// Tarjetas de relleno: nunca datos reales (lo que se ve difuminado no es información del club)
const PLACEHOLDERS = [
  { name: 'Oportunidad del club', place: 'Ubicación reservada', pct: 64 },
  { name: 'Oportunidad del club', place: 'Ubicación reservada', pct: 38 },
  { name: 'Oportunidad del club', place: 'Ubicación reservada', pct: 81 },
];

function CensoredPreview() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 select-none pointer-events-none blur-[6px] opacity-60" aria-hidden="true">
      {PLACEHOLDERS.map((p, i) => (
        <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="h-44 bg-gradient-to-br from-[#1a2742] to-[#0a1225] flex items-center justify-center">
            <Building2 className="text-[#c9a84c]/30" size={48} />
          </div>
          <div className="p-5 space-y-3">
            <p className="text-white font-semibold text-sm">{p.name}</p>
            <p className="flex items-center gap-1.5 text-gray-400 text-xs"><MapPin size={11} /> {p.place}</p>
            <div className="w-full h-1 bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full bg-[#c9a84c] rounded-full" style={{ width: `${p.pct}%` }} />
            </div>
            <div className="flex justify-between text-xs text-gray-400"><span>████ ███</span><span>██ %</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MembersOnly({ title = 'Oportunidades', children }) {
  const { can, loading, error, refreshMembership } = useAccess();

  if (can('opportunities')) return children;
  if (loading) return <GateLoading />;
  if (error) return <GateError onRetry={refreshMembership} />;

  return (
    <div className="space-y-6" data-testid="members-gate-locked">
      <div>
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        <p className="text-sm text-gray-400 mt-1">Contenido exclusivo para miembros del club</p>
      </div>
      <div className="relative">
        <CensoredPreview />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center px-6 py-8 rounded-2xl border border-[#c9a84c]/25 bg-[#0a1628]/90 backdrop-blur shadow-2xl">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/30 flex items-center justify-center">
              <Lock size={20} className="text-[#c9a84c]" />
            </div>
            <h2 className="text-white text-lg font-semibold mb-2">Esta sección es solo para miembros</h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Dentro del club, los miembros acceden a las oportunidades que se presentan a la comunidad.
              Hazte miembro para desbloquear esta sección y el resto del área privada.
            </p>
            <Link to="/dashboard#membresias" data-testid="members-gate-cta"
              className="inline-flex items-center justify-center min-h-[40px] px-5 py-2.5 rounded-lg bg-[#c9a84c] text-[#060d1b] text-sm font-semibold hover:bg-[#d8b85c]">
              Hazte miembro
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
